<template>
  <div class="home">
    <section class="hero" aria-labelledby="home-heading">
      <div class="hero-copy">
        <p class="eyebrow">Elkhorn Valley Family Medicine</p>
        <h1 id="home-heading">Rural healthcare<br />in O'Neill, Nebraska.</h1>
        <p class="hero-description">
          Elkhorn Valley Family Medicine provides complete family medicine and chiropractic care.
          Find the information you need for your next visit, or give our office a call.
        </p>
        <div class="actions">
          <a class="action action-primary" href="tel:+14023364222">
            <i class="pi pi-phone" aria-hidden="true"></i>
            Call to schedule
          </a>
          <a class="action action-secondary" href="#visit-information">Hours &amp; directions</a>
        </div>
        <p class="phone-note">Appointments by phone: <a href="tel:+14023364222">(402) 336-4222</a></p>
      </div>
      <div class="visit-preview">
        <span class="preview-icon pi pi-map-marker" aria-hidden="true"></span>
        <p class="eyebrow">Visit our clinic</p>
        <h2 id="visit-preview-heading">Location &amp; hours</h2>
        <address>304 East Douglas Street<br />O'Neill, NE 68763</address>
        <div class="preview-hours">
          <span>Family medicine</span>
          <strong>Monday – Friday</strong>
          <span>8:15 a.m. – 5:00 p.m.</span>
        </div>
        <div class="preview-hours">
          <span>Chiropractic</span>
          <strong>Monday – Friday</strong>
          <span>7:00 a.m. – 4:00 p.m.</span>
        </div>
        <a class="text-link" href="#visit-information">Plan your visit <span aria-hidden="true">→</span></a>
      </div>
    </section>

    <nav class="quick-links" aria-label="Patient quick links">
      <router-link class="quick-link" to="/patient-forms">
        <i class="pi pi-file" aria-hidden="true"></i>
        <span><strong>Patient forms</strong><span>Find forms for your visit</span></span>
        <span class="link-arrow" aria-hidden="true">→</span>
      </router-link>
      <router-link class="quick-link" to="/telemedicine">
        <i class="pi pi-video" aria-hidden="true"></i>
        <span><strong>Telemedicine</strong><span>Learn about virtual visits</span></span>
        <span class="link-arrow" aria-hidden="true">→</span>
      </router-link>
      <a class="quick-link" href="#our-providers">
        <i class="pi pi-users" aria-hidden="true"></i>
        <span><strong>Meet our providers</strong><span>Get to know your care team</span></span>
        <span class="link-arrow" aria-hidden="true">→</span>
      </a>
    </nav>

    <section class="home-section" aria-labelledby="services-heading">
      <p class="eyebrow">Rural healthcare</p>
      <h2 id="services-heading">Family medicine &amp; chiropractic care.</h2>
      <p class="section-intro">Explore our family medicine and chiropractic services.</p>
      <div class="service-grid">
        <article class="service-card">
          <h3>Family medicine</h3>
          <p>Care for infants, children, and adults, from everyday health concerns to managing chronic conditions.</p>
          <router-link class="text-link" to="/services">Explore clinical services <span aria-hidden="true">→</span></router-link>
        </article>
        <article class="service-card">
          <h3>Preventive care</h3>
          <p>Wellness visits, physicals, and immunizations to help you look after your health.</p>
          <router-link class="text-link" to="/services">View preventive services <span aria-hidden="true">→</span></router-link>
        </article>
        <article class="service-card">
          <h3>Chiropractic care</h3>
          <p>Learn about chiropractic care and meet Dr. Guy Fenske.</p>
          <router-link class="text-link" to="/providers/guy-fenske">Meet our chiropractor <span aria-hidden="true">→</span></router-link>
        </article>
      </div>
    </section>

    <section id="our-providers" class="home-section provider-section" aria-labelledby="providers-heading">
      <div>
        <p class="eyebrow">Your care team</p>
        <h2 id="providers-heading">Meet our providers.</h2>
        <p class="section-intro">Learn about their backgrounds and approach to care.</p>
      </div>
      <ul class="provider-list">
        <li v-for="provider in providers" :key="provider.path">
          <router-link :to="provider.path">
            <span>{{ provider.name }} <span class="credentials">{{ provider.credentials }}</span></span>
            <span aria-hidden="true">→</span>
          </router-link>
        </li>
      </ul>
    </section>

    <section id="visit-information" class="home-section visit-section" aria-labelledby="visit-heading">
      <p class="eyebrow">We're here to help</p>
      <h2 id="visit-heading">Plan your visit.</h2>
      <div class="visit-grid">
        <div>
          <h3>Location &amp; contact</h3>
          <address>Elkhorn Valley Family Medicine<br />304 East Douglas Street<br />O'Neill, NE 68763</address>
          <p><a href="tel:+14023364222">(402) 336-4222</a><br /><span class="secondary-text">Fax: (402) 336-4228</span></p>
          <a class="action action-secondary" href="https://www.google.com/maps/dir/?api=1&amp;destination=304%20East%20Douglas%20Street%2C%20O%27Neill%2C%20NE%2068763">
            Get directions <span aria-hidden="true">→</span>
          </a>
        </div>
        <div>
          <h3>Office hours</h3>
          <dl class="office-hours">
            <dt>Family medicine</dt>
            <dd>Monday–Friday<br />8:15 a.m.–5:00 p.m.</dd>
            <dt>Chiropractic</dt>
            <dd>Monday–Friday<br />7:00 a.m.–4:00 p.m.</dd>
          </dl>
          <p>To schedule a visit or ask about appointment availability, call our office.</p>
          <router-link class="text-link" to="/office-policies">Read office policies <span aria-hidden="true">→</span></router-link>
        </div>
      </div>
      <p class="emergency-note"><strong>Medical emergency?</strong> Call <a href="tel:911">911</a> immediately.</p>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Home',
  data() {
    return {
      providers: [
        { name: 'Peter Lueninghoener', credentials: 'MD', path: '/providers/peter-lueninghoener' },
        { name: 'Heather Pardun', credentials: 'APRN', path: '/providers/heather-pardun' },
        { name: 'Jenna Kaup', credentials: 'APRN', path: '/providers/jenna-kaup' },
        { name: 'Guy Fenske', credentials: 'DC', path: '/providers/guy-fenske' }
      ]
    }
  }
}
</script>

<style scoped>
.home {
  width: min(100% - 2.5rem, 72rem);
  margin: 0 auto;
  padding-bottom: 4rem;
  text-align: left;
  line-height: 1.65;
  overflow-wrap: anywhere;
}
.hero {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 22rem), 1fr));
  align-items: center;
  gap: 4rem;
  padding: 4rem 0 3rem;
}
h1, h2, h3 {
  margin: 0;
  line-height: 1.2;
}
h1 {
  color: var(--burgundy);
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2.4rem, 4.8vw, 4rem);
  font-weight: 400;
  letter-spacing: -.035em;
}
h2 {
  color: var(--green);
  font-size: clamp(1.6rem, 3vw, 2.15rem);
}
h3 {
  color: var(--burgundy);
  font-size: 1.2rem;
}
.hero-description {
  max-width: 34rem;
  margin: 1.5rem 0;
  font-size: 1.15rem;
  color: var(--muted);
}
.phone-note, .secondary-text {
  color: var(--muted);
  font-size: .95rem;
}
.home a:not(.action):not(.quick-link) {
  color: var(--burgundy);
}
.home a:focus-visible {
  outline: 3px solid var(--green);
  outline-offset: 4px;
}
.visit-preview {
  padding: 2rem;
  background: #edf2eb;
  border: 1px solid #d6e0d4;
  border-radius: .75rem;
}
.preview-icon {
  display: inline-block;
  margin-bottom: 1.25rem;
  color: var(--green);
  font-size: 1.75rem;
}
.visit-preview h2 {
  margin-bottom: 1.25rem;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 1.8rem;
  font-weight: 400;
}
address {
  font-style: normal;
}
.preview-hours {
  display: grid;
  gap: .1rem;
  margin: 1.25rem 0;
  padding-top: 1rem;
  border-top: 1px solid #ccd8c9;
}
.text-link {
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  min-height: 44px;
  font-weight: 700;
  text-underline-offset: .2em;
}
.quick-links, .service-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 18rem), 1fr));
  gap: 1rem;
}
.quick-link {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border: 1px solid #e3dcd6;
  border-radius: .5rem;
  background: #fff;
  color: var(--green);
  text-decoration: none;
}
.quick-link:hover {
  background: #edf2eb;
  border-color: #bdcdb9;
}
.quick-link > .pi {
  font-size: 1.4rem;
}
.quick-link > span {
  min-width: 0;
}
.quick-link strong, .quick-link strong + span {
  display: block;
}
.quick-link strong + span {
  margin-top: .2rem;
  color: var(--muted);
  font-size: .85rem;
}
.link-arrow {
  margin-left: auto;
}
.home-section {
  padding-top: 3.5rem;
  scroll-margin-top: 1.5rem;
}
.section-intro {
  margin: 1rem 0 1.5rem;
  color: var(--muted);
}
.service-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1.5rem;
  background: #fff;
  border-top: 3px solid var(--burgundy);
  border-radius: .25rem;
  box-shadow: 0 2px 12px #2c3e500a;
}
.service-card p {
  flex: 1;
}
.provider-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 24rem), 1fr));
  gap: 3rem;
  margin-top: 3.5rem;
  padding-top: 2.5rem;
  border-top: 1px solid #e3dcd6;
}
.provider-list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.provider-list a {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 48px;
  padding: .65rem 0;
  border-bottom: 1px solid #e3dcd6;
  text-decoration: none;
  font-weight: 700;
}
.provider-list a:hover {
  text-decoration: underline;
}
.credentials {
  color: var(--muted);
  font-size: .85rem;
  font-weight: 400;
}
.visit-section {
  margin-top: 3.5rem;
  padding: 2.5rem;
  background: #f3efea;
  border-radius: .75rem;
}
.visit-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 18rem), 1fr));
  gap: 3rem;
  margin-top: 2rem;
}
.visit-grid h3 {
  margin-bottom: 1rem;
}
.office-hours {
  margin: 0;
}
.office-hours dt {
  font-weight: 700;
}
.office-hours dd {
  margin: .25rem 0 1rem;
}
.emergency-note {
  margin: 2rem 0 0;
  padding-top: 1.25rem;
  border-top: 1px solid #ded6cc;
  font-size: .95rem;
}
@media screen and (max-width: 960px) {
  .hero {
    gap: 2rem;
  }
  .quick-links {
    grid-template-columns: 1fr;
  }
}
@media screen and (max-width: 700px) {
  .hero, .service-grid, .provider-section, .visit-grid {
    grid-template-columns: 1fr;
  }
  .hero {
    padding: 2.5rem 0 1.5rem;
  }
  .home-section {
    padding-top: 2.5rem;
  }
  .provider-section, .visit-section {
    margin-top: 2.5rem;
  }
  .provider-section, .visit-grid {
    gap: 1.5rem;
  }
  .visit-preview, .visit-section {
    padding: 1.5rem;
  }
}
@media screen and (max-width: 400px) {
  h1 {
    font-size: 2rem;
  }
  .actions .action {
    width: 100%;
  }
}
</style>
